export enum RooftopDamageId {
  CutsAndTears = 'cuts-and-tears',
  Punctures = 'punctures',
  AbrasionsAndScuffing = 'abrasions-and-scuffing',
  Cracks = 'cracks',
  Delamination = 'delamination',
  Alligatoring = 'alligatoring',
  BlistersAndBubbles = 'blisters-and-bubbles',
  WetInsulation = 'wet-insulation',
  MoistureIntrusionWaterPonding = 'moisture-intrusion-water-ponding',
  CapillaryMoistureIntrusion = 'capillary-moisture-intrusion',
  CondensationDamage = 'condensation-damage',
  Efflorescence = 'efflorescence',
  DepressionsLowSpots = 'depressions-low-spots',
  SaggingDeckSubstrate = 'sagging-deck-substrate',
  OpenOrUnstableJoints = 'open-or-unstable-joints',
  DelaminatedLayersCoreSeparation = 'delaminated-layers-core-separation',
  DeformationOfRoofSurface = 'deformation-of-roof-surface',
  WarpedOrBowedPanels = 'warped-or-bowed-panels',
  BackedOutFasteners = 'backed-out-fasteners',
  MissingFasteners = 'missing-fasteners',
  CorrodedOrRustedFasteners = 'corroded-or-rusted-fasteners',
  ImproperlySealedFasteners = 'improperly-sealed-fasteners',
  LooseOrDetachedFlashing = 'loose-or-detached-flashing',
  CrackedSealantAtFlashing = 'cracked-sealant-at-flashing',
  ImproperCounterflashingStepFlashingInstallation = 'improper-counterflashing-step-flashing-installation',
  SeamFailuresSplitSeams = 'seam-failures-split-seams',
  MembraneShrinkage = 'membrane-shrinkage',
  Fishmouths = 'fishmouths',
  Tentings = 'tentings',
  OpenTerminationsEdges = 'open-terminations-edges',
  ThermalBridgingInsulationExposure = 'thermal-bridging-insulation-exposure',
  UvDamageBrittleness = 'uv-damage-brittleness',
  SurfaceChalkiness = 'surface-chalkiness',
  ColorFadingDiscoloration = 'color-fading-discoloration',
  SurfaceErosion = 'surface-erosion',
  WindUpliftWindScour = 'wind-uplift-wind-scour',
  HailDamageImpactCraters = 'hail-damage-impact-craters',
  TreeLimbOrDebrisDamage = 'tree-limb-or-debris-damage',
  ConstructionMaintenanceDebris = 'construction-maintenance-debris',
  CloggedRoofDrainsScuppersGutters = 'clogged-roof-drains-scuppers-gutters',
  BlockedOverflowOutlets = 'blocked-overflow-outlets',
  ImproperDrainageSlope = 'improper-drainage-slope',
  OverflowOrBackupEvidence = 'overflow-or-backup-evidence',
  DefectivePipeBootsFlashingCollars = 'defective-pipe-boots-flashing-collars',
  LeakySkylightPerimeters = 'leaky-skylight-perimeters',
  ImproperHvacUnitMounting = 'improper-hvac-unit-mounting',
  CorrodedMechanicalSupportsOrRails = 'corroded-mechanical-supports-or-rails',
  DebrisAccumulationAroundPenetrations = 'debris-accumulation-around-penetrations',
  LooseGravelStopsEdgeFlashing = 'loose-gravel-stops-edge-flashing',
  ParapetWallDeterioration = 'parapet-wall-deterioration',
  WaterIntrusionAtCopingsCaps = 'water-intrusion-at-copings-caps',
  GapsAtExpansionJoints = 'gaps-at-expansion-joints',
  MossOrAlgaeGrowth = 'moss-or-algae-growth',
  LichenMoldStains = 'lichen-mold-stains',
  RootPenetration = 'root-penetration',
  BirdOrPestNestingDamage = 'bird-or-pest-nesting-damage',
  VegetativeDebris = 'vegetative-debris',
}

const isRooftopDamageId = (value: unknown): value is RooftopDamageId => {
  return (
    typeof value === 'string' &&
    (Object.values(RooftopDamageId) as string[]).includes(value)
  )
}

export interface RooftopFailureMode {
  failureMode: string
  evidence: string
}

export class KVRooftopAnnotation {
  section?: string
  whatWeSee?: string
  whyItMatters?: string
  likelyCause?: string
  recommendedFix?: string
  rgbScreenshotPath?: string // Path to RGB screenshot in rolodex: Screenshots/{AnnotationID}/RGB.jpg
  thermalScreenshotPath?: string // Path to thermal screenshot in rolodex: Screenshots/{AnnotationID}/Thermal.jpg
  selected: RooftopDamageId[]
  failureModes?: RooftopFailureMode[]

  constructor() {
    this.selected = []
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  static fromJSON(json: any): KVRooftopAnnotation {
    const annotation = new KVRooftopAnnotation()

    // Set section if provided
    if (json.section !== undefined) annotation.section = json.section

    // Set report text fields if provided
    if (json.whatWeSee !== undefined) annotation.whatWeSee = json.whatWeSee
    if (json.whyItMatters !== undefined)
      annotation.whyItMatters = json.whyItMatters
    if (json.likelyCause !== undefined)
      annotation.likelyCause = json.likelyCause
    if (json.recommendedFix !== undefined)
      annotation.recommendedFix = json.recommendedFix

    // Set screenshot path fields if provided (new file-based system)
    if (json.rgbScreenshotPath !== undefined)
      annotation.rgbScreenshotPath = json.rgbScreenshotPath
    if (json.thermalScreenshotPath !== undefined)
      annotation.thermalScreenshotPath = json.thermalScreenshotPath

    // Set LLM-generated fields if provided
    if (json.failureModes !== undefined)
      annotation.failureModes = json.failureModes

    // Legacy support for old base64 screenshots (convert if needed)
    if (json.rgbScreenshot !== undefined && !json.rgbScreenshotPath) {
      // Legacy base64 data exists but no file path - could migrate later
      console.warn(
        'Found legacy rgbScreenshot data - consider migrating to file-based storage'
      )
    }
    if (json.thermalScreenshot !== undefined && !json.thermalScreenshotPath) {
      // Legacy base64 data exists but no file path - could migrate later
      console.warn(
        'Found legacy thermalScreenshot data - consider migrating to file-based storage'
      )
    }

    // Handle damage types - support both new array format and legacy boolean format
    if (Array.isArray(json)) {
      // New format: array of damage IDs
      annotation.selected = json.filter(isRooftopDamageId)
    } else if (json && Array.isArray(json.selected)) {
      // New format: object with selected array
      annotation.selected = json.selected.filter(isRooftopDamageId)
    } else if (json && typeof json === 'object') {
      // Legacy format: convert boolean properties to selected array
      const selectedDamages: RooftopDamageId[] = []
      Object.values(RooftopDamageId).forEach((damageId) => {
        const propertyName = damageId.replace(/-([a-z])/g, (_, letter) =>
          letter.toUpperCase()
        )
        if (json[propertyName] === true) {
          selectedDamages.push(damageId)
        }
      })
      annotation.selected = selectedDamages
    }

    return annotation
  }

  static toJSON(
    annotation: KVRooftopAnnotation
  ): Record<string, string | string[] | RooftopFailureMode[] | undefined> {
    return {
      section: annotation.section,
      whatWeSee: annotation.whatWeSee,
      whyItMatters: annotation.whyItMatters,
      likelyCause: annotation.likelyCause,
      recommendedFix: annotation.recommendedFix,
      rgbScreenshotPath: annotation.rgbScreenshotPath,
      thermalScreenshotPath: annotation.thermalScreenshotPath,
      selected: [...annotation.selected],
      failureModes: annotation.failureModes,
    }
  }
}
